import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { InfoGrid } from '@/components/ui/InfoGrid';
import { useLayout } from '@/contexts/layout-context';
import { useTenants } from '@/store/tenant';
import { ArrowLeft, Building2, Plus, Trash2, Globe, Image as ImageIcon } from 'lucide-react';
import type { TenantContact } from '@/types/tenant';

export const CreateTenantPage = () => {
    const { setBreadcrumbs } = useLayout();
    const createTenant = useTenants((state) => state.create);
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [domain, setDomain] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [contacts, setContacts] = useState<TenantContact[]>([]);
    
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Tenants', to: '/tenants' }, { name: 'Create' }]);
    }, [setBreadcrumbs]);

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            await createTenant({ name, slug, domain, logo_url: logoUrl, contact_info: contacts });
            toast.success('Tenant created successfully');
            navigate('/m/tenants');
        } catch (err: any) {
            console.error(err);
            // Handle HTTP 409 Conflict if slug is duplicate natively from backend (if implemented)
            const msg = err?.response?.status === 409 ? 'Slug already exists.' : 'Failed to create tenant. Please try again.';
            toast.error(msg);
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const addContact = () => {
        setContacts([...contacts, { first_name: '', last_name: '', email: '', is_primary: contacts.length === 0 }]);
    };
    
    const removeContact = (index: number) => {
        setContacts(contacts.filter((_, i) => i !== index));
    };
    
    const updateContact = (index: number, field: keyof TenantContact, value: string | boolean) => {
        const newContacts = [...contacts];
        if (field === 'is_primary' && value === true) {
            newContacts.forEach(c => c.is_primary = false);
        }
        newContacts[index] = { ...newContacts[index], [field]: value };
        setContacts(newContacts);
    };

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Create Tenant"
                subtitle="Register a new vendor or organization into the platform."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/tenants')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Tenants
                    </Button>
                }
            />

            <div className="mt-8 max-w-3xl">
                <Card className="glass" title="Tenant Information" subtitle="Provide the basic identification for this tenant.">
                    {error && (
                        <div className="p-4 mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium animate-in slide-in-from-top-2">
                            {error}
                        </div>
                    )}
                    
                    <form onSubmit={submit} className="space-y-6">
                        <InfoGrid cols={2}>
                            <InputField
                                label="Tenant Name"
                                placeholder="e.g. Acme Corporation"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                icon={<Building2 className="w-4 h-4" />}
                            />
                            <InputField
                                label="Slug Identifier"
                                placeholder="e.g. acme-corp"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value)}
                                required
                                className="font-mono"
                                helpText="This will be used in URLs and identifiers. Must be unique."
                            />
                        </InfoGrid>

                        <InputField
                            label="Domain (Optional)"
                            placeholder="e.g. acme.com (FQDN only)"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            className="max-w-md font-mono"
                            icon={<Globe className="w-4 h-4" />}
                            helpText="The fully qualified domain name for this tenant."
                        />

                        <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-700 dark:text-slate-300 block">
                                Tenant Logo (Optional)
                            </label>
                            <div className="flex items-center gap-4">
                                {logoUrl ? (
                                    <div className="h-16 w-16 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0 shadow-sm p-1">
                                        <img src={logoUrl} alt="Logo preview" className="w-full h-full object-contain rounded" />
                                    </div>
                                ) : (
                                    <div className="h-16 w-16 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center shrink-0 text-slate-400">
                                        <ImageIcon className="w-5 h-5 mb-0.5 opacity-50" />
                                        <span className="text-[10px] text-center leading-tight">No logo</span>
                                    </div>
                                )}
                                <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                    <InputField
                                        value={logoUrl}
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                        placeholder="Logo URL or uploaded image data URL..."
                                        className="w-full text-[13px] py-1.5"
                                    />
                                    <label className="cursor-pointer bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 flex items-center justify-center text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0">
                                        Upload File
                                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                    </label>
                                </div>
                            </div>
                        </div>
                        
                        <div className="pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-white">Contact Information</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Add operational contacts for this tenant.</p>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={addContact} className="gap-2">
                                    <Plus className="w-4 h-4" />
                                    Add Contact
                                </Button>
                            </div>
                            
                            {contacts.length === 0 ? (
                                <div className="text-sm text-center p-6 border border-dashed rounded-lg border-slate-200 dark:border-slate-800 text-slate-500">
                                    No contacts added yet.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {contacts.map((contact, idx) => (
                                        <div key={idx} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-4 relative group">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                                                onClick={() => removeContact(idx)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                            
                                            <InfoGrid cols={2}>
                                                <InputField
                                                    label="First Name"
                                                    value={contact.first_name}
                                                    onChange={(e) => updateContact(idx, 'first_name', e.target.value)}
                                                    required
                                                />
                                                <InputField
                                                    label="Last Name"
                                                    value={contact.last_name}
                                                    onChange={(e) => updateContact(idx, 'last_name', e.target.value)}
                                                    required
                                                />
                                            </InfoGrid>
                                            <InfoGrid cols={2} className="items-end">
                                                <InputField
                                                    label="Email"
                                                    type="email"
                                                    value={contact.email}
                                                    onChange={(e) => updateContact(idx, 'email', e.target.value)}
                                                    required
                                                />
                                                <label className="flex items-center gap-2 pb-3 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        className="rounded border-slate-300 text-orange-600 focus:ring-orange-600 dark:border-slate-700 dark:bg-slate-900"
                                                        checked={contact.is_primary}
                                                        onChange={(e) => updateContact(idx, 'is_primary', e.target.checked)}
                                                    />
                                                    Primary Contact
                                                </label>
                                            </InfoGrid>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pt-8 mt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/m/tenants')}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                className="min-w-35"
                            >
                                Create Tenant
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};
